import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { startWith } from 'rxjs';
import {
  findPsaSelectItemById,
  resolvePsaSelectItemId,
} from 'src/app/helpers/utils/psa-baseline-methodology.util';
import { FORM_REGISTER } from 'src/app/pages/projects-repository/register/project/project-register.component';
import { ProjectBaselineActivityService } from 'src/app/pages/projects-repository/services/project-baseline-activity.service';
import { PsaProjectData } from 'src/app/shared/interfaces/psa-project-register';
import { getDocumentCharacterCount, formatCpfCnpjInputValue, resolveCpfCnpjInputMask } from 'src/app/helpers/utils/cpf-cnpj.util';
import { formatRegisterAccountCode } from 'src/app/helpers/utils/account.util';

@Component({
  standalone: false,
  selector: 'app-psa-step-general-data',
  templateUrl: './step-general-data.component.html',
  styleUrls: ['./step-general-data.component.scss']
})
export class PsaStepGeneralDataComponent implements OnInit {

  public today = new Date();
  public activeDate: Date = new Date();
  public maxDate: Date;
  public minDate: Date;
  public errorMsg = "Data inválida ou não preenchida";

  characterCount: number = 0;

  selectBaselineActivity: B3SelectItem[] = [];

  date: boolean = true;
  isInvalidDate: boolean = false;

  constructor(
    @Inject(FORM_REGISTER) public formRegister: FormGroup,
    @Inject('projectDraftData') public projectDraftData: PsaProjectData,
    private projectBaselineActivityService: ProjectBaselineActivityService,
  ) {
    const currentYear = new Date().getFullYear();
    this.maxDate = new Date(currentYear, 11, 31);
    this.minDate = new Date(1992, 0, 1);
  }

  ngOnInit(): void {
    this.loadBaselinesActivity();
    this.loadFormRegisterDate();

    this.formRegister.get('generalDetails')?.valueChanges
      .pipe(
        startWith(this.formRegister.get('generalDetails')?.value || '')
      )
      .subscribe(value => {
        this.characterCount = value ? value.length : 0;
      });
  }

  get developerDocumentMask(): 'text' | 'cnpj' {
    return resolveCpfCnpjInputMask(this.formRegister.get('developerCnpj')?.value);
  }

  get minDateIso(): string {
    return this.toIsoDate(this.minDate);
  }

  get maxDateIso(): string {
    return this.toIsoDate(this.today);
  }

  onProjectStartDateChange(event: Event): void {
    const iso = (event as CustomEvent<{ value?: string }>).detail?.value ?? '';
    const date = iso ? this.fromIsoDate(iso) : null;
    if (!date) {
      return;
    }
    this.date = false;
    this.isInvalidDate = date > this.today || date < this.minDate;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private fromIsoDate(iso: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  onChangesInputCpfOrCnpj(): void {
    const control = this.formRegister.get('developerCnpj');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next, { emitEvent: false });
    }
  }

  onRegisterCodeBlur(): void {
    const control = this.formRegister.get('registerCode');
    const formatted = formatRegisterAccountCode(control?.value);

    if (control?.value !== formatted) {
      control?.setValue(formatted, { emitEvent: false });
    }
  }

  loadFormRegisterDate(): void {
    if (this.projectDraftData?.projectStartDate) {
      this.loadDate(this.projectDraftData?.projectStartDate);
    }

    this.formRegister.patchValue({
      projectStartDate: this.activeDate || null,
    });
  }

  loadDate(date: string) {
    let convertedDate = this.convertStringToDate(date);
    this.activeDate = convertedDate;
    this.formRegister.get('projectStartDate')?.setValue(this.activeDate);
  }

  convertStringToDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  onBlurDate(date: Date, inputId: string) {
    this.formRegister.get(inputId)?.setValue(date);
    this.date = false;
    this.isInvalidDate = date > this.today || date < this.minDate;
  }

  checkHasError(fieldName: string, errorCode?: string): boolean {
    let field = this.formRegister.get(fieldName);
    if (field == null || !field.touched) {
      return false;
    }
    if (errorCode == null) {
      return field.invalid;
    }
    if (errorCode === 'length') {
      return this.checkHasErrorDigitCounter(fieldName);
    }
    if (errorCode === 'digit') {
      return field.hasError('digit') || field.hasError('invalidChecksum');
    }

    return field.hasError(errorCode);
  }

  checkDataError(fieldName: string, errorCode: string) {
    const fieldDate = this.formRegister.get(fieldName) || null;

    if (fieldDate?.valid) {
      return false;
    }

    return fieldDate?.hasError(errorCode) ?? true;
  }
  

  loadBaselinesActivity(): void {
    this.projectBaselineActivityService.getBaselinesActivity().subscribe(response => {
      const baselinesActivity: any[] = response?.data ?? [];

      this.selectBaselineActivity = baselinesActivity.map(baselineActivity => ({
        inputId: baselineActivity.baselineActivityIdentifier,
        label: baselineActivity.baselineActivityDescription,
        data: baselineActivity.baselineActivityIdentifier
      }));

      this.applyBaselineActivitySelection();
    });
  }

  private applyBaselineActivitySelection(): void {
    const resolved = this.resolveBaselineActivityValue();
    if (!resolved) {
      return;
    }

    const control = this.formRegister.get('baselineActivity');
    control?.setValue(resolved);
    // Re-apply after projected select options render for the DS web component.
    setTimeout(() => {
      control?.setValue(resolved);
      control?.markAsPristine();
      control?.markAsUntouched();
    });
  }

  private resolveBaselineActivityValue(): B3SelectItem | '' {
    if (!this.selectBaselineActivity.length) {
      return '';
    }

    const currentId = resolvePsaSelectItemId(this.formRegister.get('baselineActivity')?.value);
    if (currentId != null) {
      const fromForm = findPsaSelectItemById(this.selectBaselineActivity, currentId);
      if (fromForm) {
        return fromForm;
      }
    }

    const draftId = this.projectDraftData?.baselineActivityId;
    if (draftId != null) {
      const fromDraft = findPsaSelectItemById(this.selectBaselineActivity, draftId);
      if (fromDraft) {
        return fromDraft;
      }
    }

    return '';
  }

  checkHasErrorDigitCounter(fieldName: string): boolean {
    const field = this.formRegister.get(fieldName);
    const counter = getDocumentCharacterCount(field?.value);
    if (counter === 0 || counter === 11 || counter === 14) {
      return false;
    }
    return true;
  }
}